import { returnPolicyPropertiesFromDataset } from "../helpers/returnPolicyProperties";
import {
  ADD_TO_CART_SOURCES,
  cartItemTracking,
  sourceFromDataset,
  trackRemoveFromCart,
} from "../helpers/dataLayer";

const DEFAULT_MAX = 10;
const ERROR_TIMEOUT = 3000;

/**
 * Swatch selection + add to bag, for the markup rendered by the swatch-modal
 * section. Selecting a swatch never touches the cart: the bag is brought in line
 * with the selection in one go from the footer button, which adds the swatches
 * newly selected and removes the in-bag ones the shopper deselected.
 *
 * Swatches already in the bag arrive rendered selected, so the limit covers the
 * bag and the pending selection together.
 */
class SwatchModalContent extends HTMLElement {
  connectedCallback() {
    const { maxSwatches, cartSwatchCount, strings } =
      window.scrapeSectionData(this);

    this.max = parseInt(maxSwatches, 10) || DEFAULT_MAX;
    this.strings = strings || {};
    this.error = null;

    this.swatches = Array.from(this.querySelectorAll("[data-swatch]"));
    this.addButton = this.querySelector("[data-add]");
    this.messageEl = this.querySelector("[data-message]");

    // The swatches already in the bag, which are also the starting selection.
    this.inCart = new Set(
      this.swatches
        .filter((swatch) => swatch.hasAttribute("data-in-cart"))
        .map((swatch) => swatch.dataset.variantId),
    );
    this.selected = new Set(this.inCart);

    // Swatches in the bag from other style categories aren't in this grid and so
    // can't be deselected here, but they still spend against the limit.
    this.otherCount = Math.max(
      0,
      (parseInt(cartSwatchCount, 10) || 0) - this.inCart.size,
    );

    this.handleClick = (e) => {
      const swatch = e.target.closest("[data-swatch]");
      if (swatch) {
        this._toggle(swatch);
        return;
      }

      if (e.target.closest("[data-add]")) {
        this._apply();
      }
    };

    this.addEventListener("click", this.handleClick);

    this._render();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
    clearTimeout(this.errorTimeout);
  }

  _toggle(swatch) {
    const variantId = swatch.dataset.variantId;

    if (this.selected.has(variantId)) {
      this.selected.delete(variantId);
    } else {
      if (this.total >= this.max) return;

      this.selected.add(variantId);
    }

    this._render();
  }

  /**
   * Bring the bag in line with the selection. Removals go first so a swap never
   * puts the bag over the limit, even briefly.
   */
  _apply() {
    const added = this.added;
    const removed = this.removed;

    if (this.loading || (!added.length && !removed.length)) return;

    this.loading = true;
    this._render();

    this._removeLines(removed)
      .then(() => this._addLines(added))
      .then(() => {
        // Opening the mini cart also closes any open markup-modal, and the
        // trigger drops its cached markup, so this element is on its way out and
        // the next open renders against the updated bag. It stays loading until
        // then rather than offering an applied selection a second time.
        document.dispatchEvent(new CustomEvent("mini-cart:open"));
      })
      .catch((response) => {
        this.loading = false;
        this._showError(response?.message || this.strings.addToCartError);
      });
  }

  /**
   * @param {Array} variantIds swatches to add to the bag
   * @returns {Promise}
   */
  _addLines(variantIds) {
    if (!variantIds.length) return Promise.resolve();

    const items = variantIds.map((variantId) => {
      const swatch = this.swatches.find(
        (el) => el.dataset.variantId === variantId,
      );

      return {
        id: variantId,
        quantity: 1,
        properties: {
          _is_swatch: "true",
          ...returnPolicyPropertiesFromDataset(swatch?.dataset),
        },
        tracking: swatch && cartItemTracking(sourceFromDataset(swatch.dataset)),
      };
    });

    return new Promise((resolve, reject) => {
      window.addToCart(items, {
        tracking: { properties: ADD_TO_CART_SOURCES.swatch },
        onAfterAdd: resolve,
        onError: reject,
      });
    });
  }

  /**
   * A swatch is a single line of one, so its variant id identifies the line to
   * drop.
   *
   * @param {Array} variantIds in-bag swatches to remove
   * @returns {Promise}
   */
  _removeLines(variantIds) {
    if (!variantIds.length) return Promise.resolve();

    const updates = {};
    variantIds.forEach((variantId) => {
      updates[variantId] = 0;
    });

    const trackingItems = this.swatches
      .filter((el) => variantIds.includes(el.dataset.variantId))
      .map((swatch) => ({
        quantity: 1,
        tracking: cartItemTracking(sourceFromDataset(swatch.dataset)),
      }));

    return new Promise((resolve, reject) => {
      window.updateCart(
        { updates },
        {
          onAfterAdd: () => {
            trackRemoveFromCart(trackingItems);
            resolve();
          },
          onError: reject,
        },
      );
    });
  }

  _showError(message) {
    clearTimeout(this.errorTimeout);
    this.error = message;
    this._render();

    this.errorTimeout = setTimeout(() => {
      this.error = null;
      this._render();
    }, ERROR_TIMEOUT);
  }

  /**
   * Swatches the order would hold if the selection were applied: everything
   * selected here plus the in-bag swatches this grid doesn't cover.
   */
  get total() {
    return this.otherCount + this.selected.size;
  }

  /** Selected swatches that aren't in the bag yet. */
  get added() {
    return Array.from(this.selected).filter((id) => !this.inCart.has(id));
  }

  /** In-bag swatches the shopper has deselected. */
  get removed() {
    return Array.from(this.inCart).filter((id) => !this.selected.has(id));
  }

  _render() {
    const addCount = this.added.length;
    const removeCount = this.removed.length;
    const atLimit = this.total >= this.max;

    this.swatches.forEach((swatch) => {
      const selected = this.selected.has(swatch.dataset.variantId);

      swatch.classList.toggle(
        "swatch-modal-content__swatch--selected",
        selected,
      );
      swatch.setAttribute("aria-pressed", selected ? "true" : "false");
    });

    this.classList.toggle("swatch-modal-content--at-limit", atLimit);

    if (this.addButton) {
      this.addButton.disabled =
        this.loading || (addCount === 0 && removeCount === 0);

      if (addCount) {
        this.addButton.textContent = (this.strings.addCountToBag || "").replace(
          "[count]",
          addCount,
        );
      } else if (removeCount) {
        this.addButton.textContent = this.strings.updateBag || "";
      } else {
        this.addButton.textContent = this.strings.addToBag || "";
      }
    }

    if (this.messageEl) {
      const message = this.error || (atLimit ? this.strings.maxReached : "");

      this.messageEl.textContent = message || "";
    }
  }
}

if (!customElements.get("swatch-modal-content")) {
  customElements.define("swatch-modal-content", SwatchModalContent);
}
